<div class="review-page container py-4">
  <div class="d-flex justify-content-between align-items-center mb-4">
    <div>
      <h1 class="review-title mb-1">Template review</h1>
      <p class="review-subtitle mb-0">Approve or deny community submissions.</p>
    </div>
    <button type="button" class="btn btn-outline-secondary" (click)="goBack()">Back to catalogue</button>
  </div>

  <div *ngIf="loading" class="text-center py-5">
    <mat-spinner diameter="40" class="mx-auto"></mat-spinner>
  </div>

  <div *ngIf="!loading && items.length === 0" class="empty-state text-center py-5">
    <mat-icon>inbox</mat-icon>
    <p class="mb-0 mt-2">No pending templates.</p>
  </div>

  <div class="row g-4" *ngIf="!loading && items.length">
    <div class="col-lg-4">
      <div
        *ngFor="let item of items"
        class="review-item"
        [class.active]="selected?.id === item.id"
        (click)="select(item)"
      >
        <strong class="item-title">{{ item.title }}</strong>
        <div *ngIf="item.is_resubmission || item.version > 1" class="resubmit-chip">
          Resubmission · v{{ item.version }}
        </div>
        <div *ngIf="item.pending_game_name; else existingGame" class="new-game-chip">
          New game: {{ item.pending_game_name }}
        </div>
        <ng-template #existingGame>
          <div class="game-label">{{ item.game || 'Unknown game' }}</div>
        </ng-template>
        <p class="item-description">{{ item.description }}</p>
      </div>
    </div>

    <div class="col-lg-8" *ngIf="selected">
      <div class="detail-card mb-3">
        <div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
          <div class="flex-grow-1 review-edit-block">
            <ng-container *ngIf="!editing; else editMode">
              <h2 class="detail-title mb-2">{{ selected.title }}</h2>
              <div
                *ngIf="selected.is_resubmission || selected.version > 1"
                class="resubmit-banner mb-2"
              >
                <mat-icon>history</mat-icon>
                <div>
                  <div class="resubmit-banner-label">Resubmission</div>
                  <div class="resubmit-banner-hint">
                    Author revised this listing (version {{ selected.version }}). Review the changes before approving.
                  </div>
                </div>
              </div>
              <div *ngIf="selected.pending_game_name; else selectedExistingGame" class="new-game-banner">
                <mat-icon>fiber_new</mat-icon>
                <div>
                  <div class="new-game-banner-label">New game proposed</div>
                  <div class="new-game-banner-name">{{ selected.pending_game_name }}</div>
                  <div class="new-game-banner-hint">Approving will add this game to the catalogue.</div>
                </div>
              </div>
              <ng-template #selectedExistingGame>
                <div class="game-label">{{ selected.game || 'Unknown game' }}</div>
              </ng-template>
              <div
                class="revision-bar mt-2"
                *ngIf="selected.updated_at || selected.created_at"
              >
                <mat-icon>history</mat-icon>
                <span>Last updated</span>
                <time [attr.datetime]="selected.updated_at || selected.created_at">
                  {{ (selected.updated_at || selected.created_at) | date: 'd MMM y, HH:mm' }}
                </time>
              </div>
            </ng-container>

            <ng-template #editMode>
              <form [formGroup]="editForm" class="review-edit-form">
                <mat-form-field appearance="outline" class="w-100">
                  <mat-label>Title</mat-label>
                  <input matInput formControlName="title" maxlength="25" />
                </mat-form-field>

                <mat-form-field appearance="outline" class="w-100">
                  <mat-label>Game category</mat-label>
                  <mat-select formControlName="game_id" (selectionChange)="onGameSelectChange()">
                    <mat-option *ngFor="let game of games" [value]="game.id">{{ game.name }}</mat-option>
                    <mat-option [value]="newGameValue">New game…</mat-option>
                  </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline" class="w-100" *ngIf="isNewGameSelected">
                  <mat-label>New game name</mat-label>
                  <input matInput formControlName="pending_game_name" maxlength="50" />
                  <mat-hint>Approving will add this game to the catalogue.</mat-hint>
                </mat-form-field>
              </form>
            </ng-template>
          </div>
          <div class="d-flex flex-wrap gap-2 review-actions">
            <ng-container *ngIf="!editing; else editActions">
              <button
                type="button"
                class="btn btn-outline-secondary"
                [disabled]="busyId === selected.id"
                (click)="startEdit()"
              >
                Edit
              </button>
              <button
                type="button"
                class="btn btn-success"
                [disabled]="busyId === selected.id"
                (click)="approve(selected)"
              >
                Approve
              </button>
              <button
                type="button"
                class="btn btn-outline-danger"
                [disabled]="busyId === selected.id"
                (click)="deny(selected)"
              >
                Deny
              </button>
            </ng-container>
            <ng-template #editActions>
              <button
                type="button"
                class="btn btn-outline-secondary"
                [disabled]="busyId === selected.id"
                (click)="cancelEdit()"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary"
                [disabled]="busyId === selected.id || editForm.invalid"
                (click)="saveEdits()"
              >
                Save
              </button>
            </ng-template>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-section-label">Description</div>
          <p class="detail-section-body mb-0">{{ selected.description }}</p>
        </div>

        <div class="detail-section" *ngIf="selected.notes">
          <div class="detail-section-label">Notes from submitter</div>
          <p class="detail-section-body mb-0">{{ selected.notes }}</p>
        </div>
      </div>

      <div class="preview-header mb-2">Event preview</div>
      <div class="preview-container">
        <app-embed-preview></app-embed-preview>
      </div>
    </div>
  </div>

  <div class="games-card mt-4" *ngIf="!loading">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h2 class="games-title mb-1">Catalogue games</h2>
        <p class="games-subtitle mb-0">
          Games in use show their templates so you can reassign them. Unused games can be removed.
        </p>
      </div>
    </div>

    <div *ngIf="gamesLoading" class="text-center py-3">
      <mat-spinner diameter="28" class="mx-auto"></mat-spinner>
    </div>

    <div *ngIf="!gamesLoading && games.length === 0" class="games-empty">
      No games in the catalogue yet.
    </div>

    <ul *ngIf="!gamesLoading && games.length" class="games-list">
      <li *ngFor="let game of games" class="games-list-item">
        <div class="games-list-main">
          <span class="games-list-name">{{ game.name }}</span>
          <div *ngIf="game.templates?.length" class="games-template-links">
            <span class="games-template-label">Used by:</span>
            <ng-container *ngFor="let template of game.templates">
              <a
                *ngIf="template.status === 'approved'"
                class="games-template-link"
                [routerLink]="['/templates', template.id]"
              >
                {{ template.title }}
              </a>
              <span *ngIf="template.status !== 'approved'" class="games-template-plain">
                {{ template.title }} ({{ template.status }})
              </span>
            </ng-container>
          </div>
          <div *ngIf="!game.templates?.length" class="games-unused">Not used by any template</div>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-outline-danger"
          [disabled]="!!game.templates?.length || deletingGameId === game.id"
          [title]="game.templates?.length ? 'Reassign templates before removing this game' : 'Remove unused game'"
          (click)="deleteGame(game)"
        >
          Remove
        </button>
      </li>
    </ul>
  </div>
</div>
